package top.wcpe.allincore.ui.web.preview

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLCanvasElement
import top.wcpe.allincore.dsl.ir.model.UiIr
import top.wcpe.allincore.dsl.ir.model.hud.UiHudMeta
import top.wcpe.allincore.layout.engine.AvailableSpace
import top.wcpe.allincore.layout.engine.LayoutResult
import top.wcpe.allincore.layout.engine.defaultLayoutEngine
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.event.FocusManager
import top.wcpe.allincore.ui.runtime.event.InputTraceCollector
import top.wcpe.allincore.ui.runtime.event.SessionHitRegistry
import top.wcpe.allincore.ui.runtime.ir.DslExecutor
import top.wcpe.allincore.ui.runtime.ir.DslLogger
import top.wcpe.allincore.ui.runtime.ir.IrInterpreter
import top.wcpe.allincore.ui.runtime.ir.IrSession
import top.wcpe.allincore.ui.runtime.ir.api.LocalApi
import top.wcpe.allincore.ui.runtime.ir.call.ActionDispatcher
import top.wcpe.allincore.ui.runtime.ir.request.RequestRegistry
import top.wcpe.allincore.ui.runtime.ir.state.SharedStateScope
import top.wcpe.allincore.ui.runtime.reactive.Reactive
import top.wcpe.allincore.ui.runtime.reactive.Signal
import top.wcpe.allincore.ui.runtime.render.RendererRegistry
import top.wcpe.allincore.ui.runtime.render.UIRenderer
import top.wcpe.allincore.ui.web.Application
import top.wcpe.allincore.ui.web.ModelCompositionHost
import top.wcpe.allincore.ui.web.ModelCompositionRequest
import top.wcpe.allincore.ui.web.ModelCompositionResult
import top.wcpe.allincore.ui.web.PixiRenderBridge
import top.wcpe.allincore.ui.web.WebInputAdapter
import top.wcpe.allincore.ui.web.WebInputOwnerHandler
import top.wcpe.allincore.ui.web.WebRenderSurface
import top.wcpe.allincore.ui.web.WebRenderSurfaceState
import top.wcpe.allincore.ui.web.mock.InMemoryMockTraceSink
import top.wcpe.allincore.ui.web.mock.MockRequestDispatcher
import top.wcpe.allincore.ui.web.mock.MockRoute
import top.wcpe.allincore.ui.web.model.ThreePreparedModelSet
import top.wcpe.allincore.ui.web.model.ThreeStaticModelPreparer
import top.wcpe.allincore.ui.web.publishLayoutBackendReady
import kotlin.js.Promise

internal class PreviewSourceRuntime(
    val source: PreviewCandidateSource,
    val modelHost: ModelCompositionHost,
    private val disposeAction: () -> Unit = {},
) {
    private var references = 1
    private var disposed = false

    internal fun acquireBundleRetirement(): () -> Unit {
        check(!disposed) { "预览来源 runtime 已释放" }
        references += 1
        var released = false
        return {
            if (!released) {
                released = true
                release()
            }
        }
    }

    internal fun releaseOwner() = release()

    internal fun forceDispose() {
        if (disposed) return
        references = 0
        disposeRuntime()
    }

    private fun release() {
        if (disposed || references == 0) return
        references -= 1
        if (references == 0) disposeRuntime()
    }

    private fun disposeRuntime() {
        if (disposed) return
        disposed = true
        val hostFailure = runCatching(modelHost::dispose).exceptionOrNull()
        val actionFailure = runCatching(disposeAction).exceptionOrNull()
        hostFailure?.let { failure -> actionFailure?.let(failure::addSuppressed) }
        hostFailure?.let { throw it }
        actionFailure?.let { throw it }
    }
}

private class PreviewRuntimeSwitchGuard(runtime: PreviewSourceRuntime) {
    private val release = runtime.acquireBundleRetirement()
    private var disposed = false

    fun dispose() {
        if (disposed) return
        disposed = true
        release()
    }
}

internal class PreviewSourceRuntimeRouter(initial: PreviewSourceRuntime) : PreviewCandidateSource, ModelCompositionHost {
    private val switchGuards = mutableListOf<PreviewRuntimeSwitchGuard>()
    private var current = initial
    private var selected = initial
    private var generation = 1L
    private var disposed = false

    fun select(runtime: PreviewSourceRuntime) {
        if (disposed) {
            runtime.forceDispose()
            return
        }
        if (selected === runtime) {
            generation += 1
            return
        }
        if (selected !== current) selected.releaseOwner()
        selected = runtime
        generation += 1
    }

    override fun load(path: String): Promise<PreviewCandidateLoadResult> {
        if (disposed) return Promise.resolve(PreviewCandidateLoadResult.Failure(listOf(DISPOSED_DIAGNOSTIC)))
        val runtime = selected
        val captured = generation
        return runtime.source.load(path).then { result -> consume(runtime, captured, result) }.catch { reason ->
            PreviewCandidateLoadResult.Failure(listOf(safeRuntimeFailure(reason)))
        }
    }

    override fun probePublishedChanges(): Promise<Boolean> =
        if (disposed) Promise.resolve(false) else current.source.probePublishedChanges()

    override fun invalidatePublishedReadSet() {
        if (!disposed) current.source.invalidatePublishedReadSet()
    }

    override fun compose(request: ModelCompositionRequest): Promise<ModelCompositionResult> =
        if (disposed) Promise.resolve(ModelCompositionResult.Cancelled) else current.modelHost.compose(request)

    override fun bindHitRegistry(registry: SessionHitRegistry<WebInputOwnerHandler>?) {
        if (disposed) return
        // installView 发生在 runtime activate 之前，应绑定即将成为 current 的 selected host
        selected.modelHost.bindHitRegistry(registry)
        if (current !== selected) current.modelHost.bindHitRegistry(null)
    }

    override fun dispose() {
        if (disposed) return
        disposed = true
        generation += 1
        current.modelHost.bindHitRegistry(null)
        if (selected !== current) selected.modelHost.bindHitRegistry(null)
        val targets = if (selected === current) listOf(current) else listOf(selected, current)
        val actions = switchGuards.map { guard -> guard::dispose } + targets.map { runtime -> runtime::forceDispose }
        switchGuards.clear()
        runRuntimeCleanup(actions)
    }

    internal fun currentRuntimeForTest(): PreviewSourceRuntime = current

    private fun consume(
        runtime: PreviewSourceRuntime,
        captured: Long,
        result: PreviewCandidateLoadResult,
    ): PreviewCandidateLoadResult {
        if (!isSelected(runtime, captured)) return discardStale(result)
        return when (result) {
            is PreviewCandidateLoadResult.Failure -> result
            is PreviewCandidateLoadResult.Success -> wrapSuccess(runtime, captured, result)
        }
    }

    private fun wrapSuccess(
        runtime: PreviewSourceRuntime,
        captured: Long,
        loaded: PreviewCandidateLoadResult.Success,
    ): PreviewCandidateLoadResult.Success {
        val retirement = runtime.acquireBundleRetirement()
        val ownership = PreviewCandidateOwnership(
            commitAction = {
                loaded.ownership.commit()
                activate(runtime, captured)
            },
            discardAction = {
                loaded.ownership.discard()
                retirement()
            },
            budgetRetirement = loaded.ownership.budgetRetirement,
            retirements = loaded.ownership.retirements + retirement,
            confirmAction = {
                loaded.ownership.confirm()
                confirmSwitches()
            },
        )
        return PreviewCandidateLoadResult.Success(loaded.candidate, ownership)
    }

    private fun activate(runtime: PreviewSourceRuntime, captured: Long) {
        check(isSelected(runtime, captured)) { "只能激活最新预览来源 runtime" }
        val previous = current
        current = runtime
        selected = runtime
        if (previous !== runtime) {
            switchGuards += PreviewRuntimeSwitchGuard(previous)
            previous.releaseOwner()
        }
    }

    private fun confirmSwitches() {
        val actions = switchGuards.map { guard -> guard::dispose }
        switchGuards.clear()
        runRuntimeCleanup(actions)
    }

    private fun discardStale(result: PreviewCandidateLoadResult): PreviewCandidateLoadResult {
        if (result is PreviewCandidateLoadResult.Success) result.ownership.discard()
        return PreviewCandidateLoadResult.Failure(listOf(STALE_DIAGNOSTIC))
    }

    private fun isSelected(runtime: PreviewSourceRuntime, captured: Long): Boolean =
        !disposed && generation == captured && selected === runtime

    private companion object {
        const val DISPOSED_DIAGNOSTIC = "预览来源已释放"
        const val STALE_DIAGNOSTIC = "候选加载已被后续来源取代"
    }
}

private fun safeRuntimeFailure(reason: dynamic): String {
    val message = (reason as? Throwable)?.message.orEmpty()
    return if (message.any { it.code in 0x4E00..0x9FFF }) message else "预览来源读取失败"
}

private fun runRuntimeCleanup(actions: List<() -> Unit>) {
    var failure: Throwable? = null
    actions.forEach { action ->
        try {
            action()
        } catch (error: Throwable) {
            failure?.addSuppressed(error) ?: run { failure = error }
        }
    }
    failure?.let { throw it }
}

internal data class PreviewAppStatus(
    val activePath: String?,
    val sessionActive: Boolean,
    val layoutNodeCount: Int,
    val renderAvailable: Boolean,
    val disposed: Boolean,
)

/**
 * FR-UI-065 浏览器预览应用：resolver 加载 `.allin` / AICR 候选并组装 session、布局与渲染。
 * URL 与目录候选共用 resolver 主链及 bundle 生命周期，重载时 session 清空、persist 保留。
 *
 * executor 用同步模式：点击 handler 立即改 state，输入适配器合帧重绘即拿到新值。
 */
internal class PreviewApp(
    private val app: Application,
    private val width: Int,
    private val height: Int,
    private val background: PreviewBackground,
    private val candidateSource: PreviewCandidateSource,
    private val surface: WebRenderSurface? = null,
    private val directoryRuntimeFactory: ((DirectorySelection) -> PreviewSourceRuntime)? = null,
    private val dslLogger: DslLogger? = null,
) {

    private var root: UIElement<*> = emptyRoot()
    private var layout: LayoutResult? = null
    private var currentPath: String = ""
    private var isHud: Boolean = false
    private var forcedHud: Boolean? = null
    private var pollHandle: Int = -1
    private var inputAdapter: WebInputAdapter? = null
    private var lastLayoutNodeCount = 0
    private val inputTrace = InputTraceCollector()
    private val traceSink = InMemoryMockTraceSink()
    private val sharedStateApplicationScope = SharedStateScope()
    // surface 与模型宿主由应用级 surface 最终释放，bundle 仅占固定资源槽位。
    private val sharedModelHostResource = PreviewBundleResource { }
    private val sharedSurfaceResource = PreviewBundleResource { }
    private val bundleLifecycle = PreviewBundleLifecycle()
    private val candidateCoordinator = PreviewAppCandidateCoordinator(bundleLifecycle, ::buildUrlBundle)
    private val loadController = PreviewAppLoadController(candidateSource, candidateCoordinator, ::reportLoadDiagnostics)
    private val pollGeneration = PreviewLoadGeneration()
    private var currentSession: IrSession? = null
    private var controlsStarted = false
    private var disposed = false
    private val domListenerDisposers = mutableListOf<() -> Unit>()
    private val frameRenderer = PreviewFrameRenderer(
        surface?.let { owner -> PreviewFrameSurface { draw -> owner.renderFrame(draw) } },
        fallbackBridgeFactory = { PixiRenderBridge(app.stage, width.toFloat(), height.toFloat()) },
        fallbackPresent = { app.renderer.render(app.stage) },
    )

    // 预览视口（照搬 MC 两项设置）：vpW/vpH = 物理画布尺寸（窗口大小），guiScale = GUI 缩放倍数。
    // 逻辑布局尺寸 = 物理 / guiScale；stage.scale = guiScale；输入坐标 / guiScale。
    private var vpW: Int = width
    private var vpH: Int = height
    private var guiScale: Int = 1
    private var hasPreview: Boolean = false
    // 同一会话复用的渲染注册表：供 local.setTheme 写入，renderScene 读取
    private val rendererRegistry: RendererRegistry = RendererRegistry.defaultRegistry()
    private val viewportWidthSignal: Signal<Any?> = Reactive.signal(width.toDouble())
    private val viewportHeightSignal: Signal<Any?> = Reactive.signal(height.toDouble())
    private val previewLocalApi: LocalApi = object : LocalApi {
        override fun openScreen(id: String) {
            val path = resolveOpenScreenPath(id)
            if (path == null) {
                reportLoadDiagnostics(
                    listOf("预览宿主无法打开 screen「$id」：未登记映射路径（浏览器预览仅支持已知入口）"),
                )
                return
            }
            start(path)
        }

        override fun back() = Unit

        override fun close() = Unit

        override fun player(): Map<String, Signal<Any?>> = emptyMap()

        override fun viewport(): Map<String, Signal<Any?>> = mapOf(
            "width" to viewportWidthSignal,
            "height" to viewportHeightSignal,
        )
    }

    init {
        val preparer = surface?.modelCompositionHost as? ThreeStaticModelPreparer
        (candidateSource as? PreviewCandidateLoader)?.bindModelPreparer(preparer)
    }

    var onTakeOverCanvas: (() -> Unit)? = null

    val status: PreviewAppStatus
        get() = PreviewAppStatus(
            activePath = currentPath.takeIf(String::isNotEmpty),
            sessionActive = currentSession != null,
            layoutNodeCount = lastLayoutNodeCount,
            renderAvailable = surface == null || surface.state == WebRenderSurfaceState.READY,
            disposed = disposed,
        )

    /** 启动 URL 候选加载、热更新轮询与 playwright 锚。 */
    fun start(allinPath: String) {
        currentPath = allinPath
        requestPreview()
        ensurePreviewControls()
    }

    private fun ensurePreviewControls() {
        ensureRenderUnavailablePlaceholder()
        if (controlsStarted) return
        controlsStarted = true
        exposePreviewAnchors()
        buildRebuildButton()
        buildModeToggleButton()
        buildViewportControls()
        startPolling()
    }

    private fun ensureRenderUnavailablePlaceholder() {
        if (surface == null || surface.state == WebRenderSurfaceState.READY) return
        if (document.getElementById(RENDER_PLACEHOLDER_ID) != null) return
        val placeholder = document.createElement("div")
        placeholder.id = RENDER_PLACEHOLDER_ID
        placeholder.setAttribute("role", "status")
        placeholder.setAttribute("data-preview-render-placeholder", "true")
        placeholder.textContent = RENDER_UNAVAILABLE_MESSAGE
        val style = placeholder.asDynamic().style
        style.width = "${width}px"
        style.height = "${height}px"
        style.display = "grid"
        style.placeItems = "center"
        style.padding = "16px"
        style.boxSizing = "border-box"
        style.background = "#181922"
        style.border = "1px dashed #6B7085"
        style.color = "#FFD9A8"
        style.font = "14px monospace"
        style.textAlign = "center"
        document.body?.appendChild(placeholder)
    }

    private fun exposePreviewAnchors() {
        val win = window.asDynamic()
        win.__previewMode = { if (isHud) "hud" else "screen" }
        win.__previewName = { displayNameOf(currentPath) }
        win.__previewState = { key: String -> currentSession?.states?.get(key)?.get() }
        win.__previewPersist = { key: String ->
            currentSession?.evalContext?.sharedStateAccess?.persistSignal(key, null)?.get()
        }
        win.__previewInputTrace = { inputTrace.snapshot().toTypedArray() }
        win.__previewReload = { reload() }
        win.__previewToggleMode = { toggleMode() }
    }

    /** 关闭加载、bundle、轨迹、帧与输入资源；每个阶段失败后继续清理。 */
    fun dispose() {
        if (disposed) return
        disposed = true
        stopPolling()
        val failures = mutableListOf<Throwable>()
        disposeStep(failures, loadController::dispose)
        disposeStep(failures) { background.replaceImage(null) }
        disposeStep(failures, bundleLifecycle::dispose)
        disposeStep(failures) { (candidateSource as? PreviewSourceRuntimeRouter)?.dispose() }
        disposeStep(failures, traceSink::clearAll)
        disposeStep(failures, inputTrace::clear)
        disposeStep(failures, pollGeneration::dispose)
        disposeStep(failures, frameRenderer::dispose)
        disposeStep(failures, ::uninstallCurrentInput)
        disposeStep(failures, ::disposeShell)
        currentSession = null
        layout = null
        lastLayoutNodeCount = 0
        reportDisposeFailures(failures)
    }

    private fun stopPolling() {
        if (pollHandle < 0) return
        window.clearInterval(pollHandle)
        pollHandle = -1
    }

    private fun uninstallCurrentInput() {
        if (surface != null) surface.uninstallInputAdapter() else inputAdapter?.uninstall()
        inputAdapter = null
    }

    private fun disposeShell() {
        val actions = domListenerDisposers.asReversed().toMutableList()
        domListenerDisposers.clear()
        listOf(CONTROLS_ID, SIDEBAR_ID, DIAG_ID, RENDER_PLACEHOLDER_ID, REBUILD_ID, MODE_TOGGLE_ID)
            .forEach { id -> actions += { document.getElementById(id)?.asDynamic()?.remove() } }
        actions += {
            val win = window.asDynamic()
            win.__previewMode = null
            win.__previewName = null
            win.__previewState = null
            win.__previewPersist = null
            win.__previewInputTrace = null
            win.__previewReload = null
            win.__previewToggleMode = null
            controlsStarted = false
        }
        runRuntimeCleanup(actions)
    }

    private fun listen(target: dynamic, type: String, handler: (dynamic) -> Unit) {
        target.addEventListener(type, handler)
        domListenerDisposers += { target.removeEventListener(type, handler) }
    }

    /** 设物理画布尺寸（窗口大小）：重设渲染器 + 重绘；grid 布局自动重新居中画布。 */
    private fun setViewport(w: Int, h: Int) {
        vpW = w
        vpH = h
        val placeholderStyle = document.getElementById(RENDER_PLACEHOLDER_ID)?.asDynamic()?.style
        placeholderStyle?.width = "${w}px"
        placeholderStyle?.height = "${h}px"
        if (surface != null) surface.resize(w, h) else app.asDynamic().renderer.resize(w, h)
        if (hasPreview) renderScene()
    }

    /** 设 GUI 缩放倍数（1~4）：立即重绘（逻辑尺寸随之变、stage 放大随之变）。 */
    private fun setGuiScale(scale: Int) {
        guiScale = scale
        if (hasPreview) renderScene()
    }

    /** 视口控件（DOM，固定顶部避开画布）：窗口大小 + GUI 缩放两个下拉，照搬 MC 屏幕尺寸 / GUI Scale 设置。 */
    private fun buildViewportControls() {
        document.getElementById(CONTROLS_ID)?.asDynamic()?.remove()
        val bar = document.createElement("div")
        bar.id = CONTROLS_ID
        val bs = bar.asDynamic().style
        bs.position = "fixed"
        bs.top = "8px"
        bs.left = "240px"
        bs.zIndex = "9999"
        bs.display = "flex"
        bs.gap = "6px"
        bs.alignItems = "center"
        bs.font = "12px monospace"
        bs.color = "#9AA0B0"

        bar.appendChild(controlLabel("窗口"))
        val sizeSel = document.createElement("select")
        styleSelect(sizeSel)
        for (opt in arrayOf("400x400", "640x480", "854x480", "1280x720")) {
            val o = document.createElement("option")
            o.setAttribute("value", opt)
            o.textContent = opt
            sizeSel.appendChild(o)
        }
        listen(sizeSel, "change") {
            val parts = (sizeSel.asDynamic().value as String).split("x")
            setViewport(parts[0].toInt(), parts[1].toInt())
        }
        bar.appendChild(sizeSel)

        bar.appendChild(controlLabel("缩放"))
        val scaleSel = document.createElement("select")
        styleSelect(scaleSel)
        for (n in 1..4) {
            val o = document.createElement("option")
            o.setAttribute("value", n.toString())
            o.textContent = "${n}x"
            scaleSel.appendChild(o)
        }
        listen(scaleSel, "change") {
            setGuiScale((scaleSel.asDynamic().value as String).toInt())
        }
        bar.appendChild(scaleSel)

        document.body?.appendChild(bar)
    }

    /** 视口控件文字标签。 */
    private fun controlLabel(text: String): org.w3c.dom.Element {
        val l = document.createElement("span")
        l.textContent = text
        return l
    }

    /** 下拉框统一样式。 */
    private fun styleSelect(el: org.w3c.dom.Element) {
        val s = el.asDynamic().style
        s.background = "#2A2C36"
        s.color = "#E8E8F0"
        s.border = "1px solid #444"
        s.borderRadius = "4px"
        s.padding = "2px 4px"
        s.font = "12px monospace"
        s.cursor = "pointer"
    }

    /** 手动切换 screen / HUD 模式（兜底自动识别）：锁定 [forcedHud] 并立即重绘。 */
    private fun toggleMode() {
        forcedHud = !isHud
        isHud = forcedHud == true
        window.asDynamic().__previewMode = { if (isHud) "hud" else "screen" }
        renderScene()
    }

    /** FR-UI-065：目录只切换 resolver-backed runtime，文件内容由候选主链按需读取。 */
    fun loadDirectory(selection: DirectorySelection) {
        val router = candidateSource as? PreviewSourceRuntimeRouter
        val factory = directoryRuntimeFactory
        if (router == null || factory == null) {
            selection.dispose()
            showDiagnostics(listOf("目录预览来源尚未接入统一 resolver"))
            return
        }
        val rootFile = RootAllinSelector.select(selection.files)
        if (rootFile == null) {
            selection.dispose()
            return
        }
        val files = selection.files
        val runtime = createDirectoryRuntime(selection, factory) ?: return
        router.select(runtime)
        buildSidebar(files)
        ensurePreviewControls()
        previewFromDir(rootFile)
        takeOverCanvas()
    }

    private fun createDirectoryRuntime(
        selection: DirectorySelection,
        factory: (DirectorySelection) -> PreviewSourceRuntime,
    ): PreviewSourceRuntime? = runCatching { factory(selection) }.getOrElse { error ->
        selection.dispose()
        showDiagnostics(listOf("目录预览来源创建失败：${error.message ?: "未知错误"}"))
        null
    }

    private fun previewFromDir(file: DirFile) {
        currentPath = file.relPath
        highlightSidebar(file.relPath)
        requestPreview()
    }

    private fun takeOverCanvas() {
        onTakeOverCanvas?.invoke()
        onTakeOverCanvas = null
    }

    private fun buildUrlBundle(
        candidate: PreviewCandidate,
        identity: PreviewBundleIdentity,
    ): PreviewBundleOwnership = buildBundle(
        candidate.ir,
        identity,
        candidate.mockRoutes,
        candidate.background,
        candidate.preparedModels,
    )

    private fun buildBundle(
        ir: UiIr,
        identity: PreviewBundleIdentity,
        routes: Map<String, MockRoute>,
        candidateBackground: PreviewCandidateBackground? = null,
        preparedModels: ThreePreparedModelSet? = null,
    ): PreviewBundleOwnership {
        val parts = createSessionParts(ir, identity, routes)
        return try {
            val view = createCandidateView(ir, identity, parts.session, candidateBackground, preparedModels)
            createBundleOwnership(parts, view)
        } catch (error: Throwable) {
            preserveFailure(error) { disposeSession(parts.dispatcher, parts.session) }
            throw error
        }
    }

    private fun createSessionParts(
        ir: UiIr,
        identity: PreviewBundleIdentity,
        routes: Map<String, MockRoute>,
    ): SessionParts {
        val scope = sharedStateApplicationScope.newCandidateScope()
        var session: IrSession? = null
        val dispatcher = createDispatcher(identity, routes, scope) {
            if (currentSession === session) renderScene()
        }
        return try {
            val registry = RequestRegistry()
            val created = interpretCandidate(ir, dispatcher, registry)
            session = created
            SessionParts(scope, dispatcher, registry, created)
        } catch (error: Throwable) {
            preserveFailure(error, dispatcher::dispose)
            throw error
        }
    }

    private fun createDispatcher(
        identity: PreviewBundleIdentity,
        routes: Map<String, MockRoute>,
        scope: SharedStateScope,
        onCallback: () -> Unit,
    ): MockRequestDispatcher = MockRequestDispatcher(
        scenario = routes,
        sessionId = identity.sessionId,
        traceSink = traceSink,
        sharedStateAccess = scope,
        onCallbackDelivered = onCallback,
    )

    private fun interpretCandidate(
        ir: UiIr,
        dispatcher: MockRequestDispatcher,
        registry: RequestRegistry,
    ): IrSession {
        val session = IrInterpreter.interpretSession(
            ir,
            ActionDispatcher.NOOP,
            dispatcher,
            registry,
            previewLocalApi,
            dslLogger,
            null,
            DslExecutor.synchronous(),
            null,
        )
        // 绑定后 local.setTheme 才能写 controlTheme；必须与 renderScene 使用同一实例
        session.bindRendererRegistry(rendererRegistry)
        return session
    }

    private fun createCandidateView(
        ir: UiIr,
        identity: PreviewBundleIdentity,
        session: IrSession,
        candidateBackground: PreviewCandidateBackground?,
        preparedModels: ThreePreparedModelSet?,
    ): CandidateView {
        val focus = FocusManager()
        val hits = SessionHitRegistry<WebInputOwnerHandler>()
        val adapter = createInputAdapter(session, identity, focus, hits)
        val backgroundLoad = candidateBackground?.let(background::prepare)
        return CandidateView(
            session,
            forcedHud ?: (ir.hudMeta !== UiHudMeta.EMPTY),
            adapter,
            hits,
            backgroundLoad,
            preparedModels,
        )
    }

    private fun createInputAdapter(
        session: IrSession,
        identity: PreviewBundleIdentity,
        focus: FocusManager,
        hits: SessionHitRegistry<WebInputOwnerHandler>,
    ): WebInputAdapter? {
        val canvas = if (surface == null) app.canvas else surface.canvas ?: return null
        return WebInputAdapter(
            canvas.unsafeCast<HTMLCanvasElement>(),
            { session.root },
            { layout },
            ::renderScene,
            { guiScale.toDouble() },
            { focus },
            { identity.sessionId.toString() },
            { hits },
            { inputTrace },
        )
    }

    private fun createBundleOwnership(parts: SessionParts, view: CandidateView): PreviewBundleOwnership =
        PreviewBundleOwnership(
            freezeDeliveries = { freezeBundle(parts.dispatcher, view) },
            resumeDeliveries = { resumeBundle(parts, view) },
            activate = { activateBundle(parts, view) },
            beforeCurrentSwitch = { view.preparedModels?.activate() },
            afterCurrentSwitch = {
                view.preparedModels?.confirmActivation()
                background.replaceImage(view.background)
                clearDiagnostics()
                renderScene()
            },
            input = PreviewBundleResource { disposeInput(view) },
            modelHosts = listOf(sharedModelHostResource) + preparedModelResources(view),
            surface = sharedSurfaceResource,
            mockSession = PreviewBundleResource { disposeBundleSession(parts, view) },
            resolverLeases = view.background?.let { load ->
                listOf(PreviewBundleResource(load::dispose))
            }.orEmpty(),
        )

    private fun preparedModelResources(view: CandidateView): List<PreviewBundleResource> =
        view.preparedModels?.let { listOf(PreviewBundleResource(it::dispose)) }.orEmpty()

    private fun freezeBundle(dispatcher: MockRequestDispatcher, view: CandidateView) {
        dispatcher.freeze()
        view.adapter?.uninstall()
    }

    private fun activateBundle(parts: SessionParts, view: CandidateView) {
        parts.scope.activate()
        parts.dispatcher.bind(parts.registry) { parts.session.evalContext }
        inputTrace.clear()
        installView(view)
    }

    private fun resumeBundle(parts: SessionParts, view: CandidateView) {
        parts.scope.activate()
        parts.dispatcher.resume()
        installView(view)
        background.replaceImage(view.background)
        renderScene()
    }

    private fun installView(view: CandidateView) {
        currentSession = view.session
        root = view.session.root
        isHud = view.isHud
        hasPreview = true
        inputAdapter = view.adapter
        // FR-UI-066：把当前 session 的 hit 注册表交给 model host 注册交互 owner
        resolveModelHost()?.bindHitRegistry(view.hits)
        view.adapter?.let { adapter ->
            if (surface != null) surface.installInputAdapter(adapter) else adapter.install()
        }
    }

    private fun disposeInput(view: CandidateView) {
        if (inputAdapter === view.adapter) {
            resolveModelHost()?.bindHitRegistry(null)
        }
        view.hits.clear()
        view.adapter?.uninstall()
        if (inputAdapter === view.adapter) inputAdapter = null
    }

    private fun resolveModelHost(): ModelCompositionHost? =
        surface?.modelCompositionHost ?: (candidateSource as? ModelCompositionHost)

    private fun disposeBundleSession(parts: SessionParts, view: CandidateView) {
        clearViewIfCurrent(view)
        disposeSession(parts.dispatcher, parts.session)
    }

    private fun disposeSession(dispatcher: MockRequestDispatcher, session: IrSession) {
        val failures = mutableListOf<Throwable>()
        disposeStep(failures, dispatcher::dispose)
        disposeStep(failures, session::cancelPendingAwaits)
        disposeStep(failures, session::runCloseIfPresent)
        disposeStep(failures, session::dispose)
        throwCombined(failures)
    }

    private fun clearViewIfCurrent(view: CandidateView) {
        if (currentSession !== view.session) return
        resolveModelHost()?.bindHitRegistry(null)
        view.hits.clear()
        currentSession = null
        root = emptyRoot()
        layout = null
        lastLayoutNodeCount = 0
        isHud = false
        hasPreview = false
    }

    /** 重算布局 + 重绘（HUD 模式先画模拟画面背景层）。逻辑尺寸 = 物理 / guiScale，stage 整体放大 guiScale 倍。 */
    private fun renderScene() {
        val logicalW = (vpW / guiScale).toFloat()
        val logicalH = (vpH / guiScale).toFloat()
        // 同步 viewport Signal，驱动 wiki 等依赖 local.viewport 的布局表达式
        viewportWidthSignal.set(logicalW.toDouble())
        viewportHeightSignal.set(logicalH.toDouble())
        if (surface != null) {
            surface.setLogicalViewport(logicalW, logicalH, guiScale.toFloat())
        } else {
            app.stage.asDynamic().scale.set(guiScale)
        }
        val computed = defaultLayoutEngine()
            .compute(root, AvailableSpace.definite(logicalW, logicalH))
        layout = computed
        lastLayoutNodeCount = countLayoutNodes(root, computed)
        publishLayoutBackendReady(root, computed)
        frameRenderer.render(logicalW, logicalH) { bridge ->
            app.stage.removeChildren()
            if (isHud) {
                background.resize(logicalW.toInt(), logicalH.toInt())
                background.render(bridge)
            }
            UIRenderer.render(root, computed, bridge, rendererRegistry)
        }
    }

    private fun countLayoutNodes(element: UIElement<*>, result: LayoutResult): Int =
        (if (result.boxOf(element) != null) 1 else 0) + element.children().sumOf { child -> countLayoutNodes(child, result) }

    /** URL 与 Pages 请求只进入 resolver-backed 候选控制器。 */
    private fun requestPreview() {
        if (currentPath.isNotEmpty()) loadController.load(currentPath)
    }

    /** 手动重建会使当前权威读取集全部失效。 */
    private fun reload() {
        candidateSource.invalidatePublishedReadSet()
        requestPreview()
    }

    /** 热更新轮询只检查 resolver 已发布权威读取集的内容版本。 */
    private fun startPolling() {
        pollHandle = window.setInterval({ pollCheck() }, POLL_INTERVAL_MS)
    }

    /** 轮询探测不进入 candidate；版本变化后才触发受 generation 保护的加载。 */
    private fun pollCheck() {
        val generation = pollGeneration.next()
        val requestedPath = currentPath
        candidateSource.probePublishedChanges().then { changed ->
            if (changed && pollGeneration.isCurrent(generation) && requestedPath == currentPath) {
                loadController.load(requestedPath)
            }
            Unit
        }.catch {
            // 轮询失败静默，下轮重试。
            Unit
        }
    }

    /** 手动强制重建按钮（DOM，固定右上角）：调 [reload] 立即重取 + 重建。 */
    private fun buildRebuildButton() {
        val btn = document.createElement("button")
        btn.textContent = "重建"
        btn.id = REBUILD_ID
        styleControlButton(btn, right = "8px")
        listen(btn, "click") { reload() }
        document.body?.appendChild(btn)
    }

    /** 手动切换模式按钮（DOM）：兜底源码自动识别，供预览调试看另一模式。 */
    private fun buildModeToggleButton() {
        val btn = document.createElement("button")
        btn.textContent = "切模式"
        btn.id = MODE_TOGGLE_ID
        styleControlButton(btn, right = "116px")
        listen(btn, "click") { toggleMode() }
        document.body?.appendChild(btn)
    }

    // ---- 目录文件侧栏（DOM，左侧固定，持久，点击切换预览）----

    private fun buildSidebar(files: List<DirFile>) {
        document.getElementById(SIDEBAR_ID)?.asDynamic()?.remove()
        val bar = document.createElement("div")
        bar.id = SIDEBAR_ID
        val bs = bar.asDynamic().style
        bs.position = "fixed"
        bs.left = "0"
        bs.top = "0"
        bs.bottom = "0"
        bs.width = "220px"
        bs.overflowY = "auto"
        bs.padding = "8px"
        bs.background = "rgba(20,21,28,.94)"
        bs.borderRight = "1px solid #3A3C48"
        bs.zIndex = "9998"
        bs.font = "12px monospace"

        val title = document.createElement("div")
        title.textContent = "目录（${files.size} 个 .allin）"
        val ts = title.asDynamic().style
        ts.color = "#9AA0B0"
        ts.margin = "2px 4px 8px"
        bar.appendChild(title)

        for (file in files) {
            val item = document.createElement("button")
            item.textContent = file.relPath
            item.setAttribute("data-path", file.relPath)
            styleSidebarItem(item)
            listen(item, "click") { previewFromDir(file) }
            bar.appendChild(item)
        }
        document.body?.appendChild(bar)
    }

    private fun highlightSidebar(relPath: String) {
        val bar = document.getElementById(SIDEBAR_ID) ?: return
        val items = bar.asDynamic().querySelectorAll("button")
        val n = items.length as Int
        for (i in 0 until n) {
            val item = items[i]
            val active = item.getAttribute("data-path") == relPath
            item.style.background = if (active) "#3B6EA5" else "#2A2C36"
        }
    }

    private fun styleSidebarItem(el: org.w3c.dom.Element) {
        val s = el.asDynamic().style
        s.display = "block"
        s.width = "100%"
        s.textAlign = "left"
        s.margin = "2px 0"
        s.padding = "4px 6px"
        s.background = "#2A2C36"
        s.color = "#E8E8F0"
        s.border = "1px solid #3A3C48"
        s.borderRadius = "3px"
        s.cursor = "pointer"
        s.whiteSpace = "nowrap"
        s.overflow = "hidden"
        s.textOverflow = "ellipsis"
    }

    // ---- 诊断：编译报错（Failure）建底部可关闭面板显示，避开居中画布 + 左侧栏，不遮挡预览；warning 仍只 console ----

    /** 显示编译错误：底部固定面板（可 × 关闭、可滚动），同时打 console 便于排查。 */
    private fun showDiagnostics(lines: List<String>) {
        lines.forEach { console.error("[preview] 诊断: $it") }
        document.getElementById(DIAG_ID)?.asDynamic()?.remove()
        val panel = document.createElement("div")
        panel.id = DIAG_ID
        val ps = panel.asDynamic().style
        ps.position = "fixed"
        ps.left = "240px"
        ps.right = "12px"
        ps.bottom = "12px"
        ps.maxHeight = "30vh"
        ps.overflowY = "auto"
        ps.padding = "10px 12px"
        ps.background = "rgba(60,22,22,.96)"
        ps.border = "1px solid #A05050"
        ps.borderRadius = "6px"
        ps.zIndex = "9997"
        ps.font = "12px monospace"
        ps.color = "#FFD4D4"
        ps.boxShadow = "0 4px 20px rgba(0,0,0,.5)"

        val head = document.createElement("div")
        head.textContent = "编译错误（${lines.size}）"
        val hs = head.asDynamic().style
        hs.fontWeight = "bold"
        hs.marginBottom = "6px"
        hs.paddingRight = "24px"

        val close = document.createElement("button")
        close.textContent = "×"
        val cs = close.asDynamic().style
        cs.position = "absolute"
        cs.top = "6px"
        cs.right = "8px"
        cs.width = "20px"
        cs.height = "20px"
        cs.lineHeight = "16px"
        cs.padding = "0"
        cs.background = "transparent"
        cs.color = "#FFD4D4"
        cs.border = "1px solid #A05050"
        cs.borderRadius = "3px"
        cs.cursor = "pointer"
        listen(close, "click") { clearDiagnostics() }
        panel.appendChild(close)
        panel.appendChild(head)

        for (line in lines) {
            val item = document.createElement("div")
            item.textContent = line
            val ls = item.asDynamic().style
            ls.margin = "2px 0"
            ls.whiteSpace = "pre-wrap"
            ls.wordBreak = "break-word"
            panel.appendChild(item)
        }
        document.body?.appendChild(panel)
    }

    /** 清除错误面板（编译成功或用户手动关闭时）。 */
    private fun clearDiagnostics() {
        document.getElementById(DIAG_ID)?.asDynamic()?.remove()
    }

    private fun reportLoadDiagnostics(lines: List<String>) {
        if (bundleLifecycle.currentIdentity == null) resetInitialPreview()
        showDiagnostics(lines)
    }

    private fun resetInitialPreview() {
        runCatching(::uninstallCurrentInput)
        currentSession = null
        root = emptyRoot()
        layout = null
        isHud = false
        hasPreview = false
        runCatching(::renderScene)
    }

    private fun disposeStep(failures: MutableList<Throwable>, action: () -> Unit) {
        try {
            action()
        } catch (error: Throwable) {
            failures += error
        }
    }

    private fun throwCombined(failures: List<Throwable>) {
        val primary = failures.firstOrNull() ?: return
        failures.drop(1).forEach(primary::addSuppressed)
        throw primary
    }

    private fun preserveFailure(primary: Throwable, cleanup: () -> Unit) {
        try {
            cleanup()
        } catch (cleanupError: Throwable) {
            primary.addSuppressed(cleanupError)
        }
    }

    private fun reportDisposeFailures(failures: List<Throwable>) {
        val primary = failures.firstOrNull() ?: return
        failures.drop(1).forEach(primary::addSuppressed)
        console.error("预览资源清理失败，已继续尝试释放其余资源", primary)
    }

    private fun emptyRoot(): UIElement<*> = top.wcpe.allincore.ui.runtime.element.Box.create()

    /** openScreen 仅映射浏览器预览已知入口，避免任意 id 误拼路径。 */
    private fun resolveOpenScreenPath(id: String): String? = when (id) {
        "wiki/index", "wiki", "museum" -> "examples/wiki/index.allin"
        "demo/index", "demo" -> "examples/demo/index.allin"
        else -> null
    }

    private data class SessionParts(
        val scope: SharedStateScope,
        val dispatcher: MockRequestDispatcher,
        val registry: RequestRegistry,
        val session: IrSession,
    )

    private data class CandidateView(
        val session: IrSession,
        val isHud: Boolean,
        val adapter: WebInputAdapter?,
        val hits: SessionHitRegistry<WebInputOwnerHandler>,
        val background: PreviewBackgroundLoad?,
        val preparedModels: ThreePreparedModelSet?,
    )

    companion object {
        private const val POLL_INTERVAL_MS = 1000
        private const val SIDEBAR_ID = "preview-sidebar"
        private const val DIAG_ID = "preview-diagnostics"
        private const val CONTROLS_ID = "preview-viewport-controls"
        private const val REBUILD_ID = "preview-rebuild"
        private const val MODE_TOGGLE_ID = "preview-toggle-mode"
        private const val RENDER_PLACEHOLDER_ID = "preview-render-unavailable"
        private const val RENDER_UNAVAILABLE_MESSAGE =
            "当前浏览器无法初始化 WebGL 渲染区域，预览画面已停用；源码编译、布局与预览控制仍可使用。"
    }
}

internal fun interface PreviewFrameSurface {
    fun renderFrame(draw: (PixiRenderBridge) -> Unit): Boolean
}

/** 生产路径委托 surface 的唯一桥；无 surface 时复用并显式释放单一回退桥。 */
internal class PreviewFrameRenderer(
    private val surface: PreviewFrameSurface?,
    private val fallbackBridgeFactory: () -> PixiRenderBridge,
    private val fallbackPresent: () -> Unit,
) {
    private var fallbackBridge: PixiRenderBridge? = null

    fun render(width: Float, height: Float, draw: (PixiRenderBridge) -> Unit): Boolean {
        surface?.let { return it.renderFrame(draw) }
        val bridge = fallbackBridge ?: fallbackBridgeFactory().also { fallbackBridge = it }
        bridge.resize(width, height)
        bridge.beginFrame()
        return try {
            draw(bridge)
            fallbackPresent()
            true
        } finally {
            bridge.endFrame()
        }
    }

    fun dispose() {
        val bridge = fallbackBridge ?: return
        if (bridge.dispose()) fallbackBridge = null
    }
}

/** 预览控制按钮统一样式（固定顶部，[right] 定水平位）。 */
internal fun styleControlButton(btn: org.w3c.dom.Element, right: String) {
    val s = btn.asDynamic().style
    s.position = "fixed"
    s.top = "8px"
    s.right = right
    s.zIndex = "9999"
    s.padding = "4px 10px"
    s.font = "12px monospace"
    s.background = "#2A2C36"
    s.color = "#E8E8F0"
    s.border = "1px solid #444"
    s.borderRadius = "4px"
    s.cursor = "pointer"
}
